/**
 * Forgot password page — public, no auth.
 * Always shows success even if email doesn't exist (server returns 200 either way).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ForgotPasswordPage() {
    const { api } = useAuth();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await api.post("/v2/auth/forgot-password", { email: email.trim() });
            setDone(true);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="forgot-password-page">
            <div className="w-full max-w-sm border border-border/40 bg-card p-7">
                <Link to="/login" className="text-xs text-gray-500 hover:text-white inline-flex items-center gap-1 mb-4">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to login
                </Link>
                <h1 className="text-xl font-display font-bold text-white mb-1">Reset your password</h1>
                <p className="text-xs text-gray-500 font-body mb-5">Enter your email — we'll send a one-shot reset link valid for 1 hour.</p>

                {done ? (
                    <div className="space-y-3" data-testid="forgot-success">
                        <div className="flex items-center gap-2 text-emerald-300">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm">Check your inbox</span>
                        </div>
                        <p className="text-xs text-gray-500">If an account exists for <code className="text-white font-mono">{email}</code>, a reset link is on its way. The link expires in 1 hour.</p>
                        <Link to="/login" className="inline-block mt-2 text-xs text-gray-400 hover:text-white">Return to login →</Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase text-gray-500">Email</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                                data-testid="forgot-email"
                                className="w-full mt-1 bg-background border border-border/40 text-sm text-white px-2 py-2 focus:outline-none focus:border-white/30" />
                        </div>
                        {error && <p className="text-xs text-red-300">{error}</p>}
                        <button type="submit" disabled={busy || !email.trim()}
                            data-testid="forgot-submit"
                            className="w-full py-2 bg-white text-black text-sm font-semibold disabled:opacity-40 hover:bg-gray-200 inline-flex items-center justify-center gap-2">
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Send reset link
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
